import { Link } from 'react-router-dom';
import { useMe, useMySubscriptions } from '@/hooks/useUser';
import { formatMoney } from '@/lib/format';
import { UserHeader } from '@/components/user/UserHeader';
import { ReviewsList } from '@/components/user/ReviewsList';
import { Stat } from '@/components/ui/Stat';
import { Avatar } from '@/components/ui/Avatar';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import styles from './ProfilePage.module.css';

export const ProfilePage = () => {
  const { data: me, isLoading } = useMe();
  const { data: subs } = useMySubscriptions();

  if (isLoading || !me) {
    return (
      <div className={styles.page}>
        <div className={styles.headerSkeleton}>
          <Skeleton width={96} height={96} rounded="full" />
          <Skeleton width={140} height={22} />
          <Skeleton width={80} height={14} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <UserHeader
        name={me.display_name}
        username={me.telegram_username}
        avatarUrl={me.avatar_url}
        rating={me.rating}
        ratingCount={me.rating_count}
        memberSince={me.created_at}
      />

      <Link to="/faq" className={styles.helpLink}>
        <span className={styles.helpIcon} aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <path d="M12 17h.01" />
          </svg>
        </span>
        <span className={styles.helpText}>
          <span className={styles.helpTitle}>Как пользоваться</span>
          <span className={styles.helpSub}>Ставки, автоставка, сделки и уведомления</span>
        </span>
        <svg className={styles.helpArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="m9 18 6-6-6-6" />
        </svg>
      </Link>

      {/* Как покупатель */}
      <Section title="Как покупатель">
        <div className={styles.statsGrid}>
          <Stat label="Участий" value={me.bids_placed} />
          <Stat label="Побед" value={me.auctions_won} variant="accent" />
          <Stat label="Win-rate" value={`${me.win_rate}%`} />
          <Stat
            label="Потрачено"
            value={me.total_spent > 0 ? formatMoney(me.total_spent) : '—'}
            size="sm"
          />
        </div>
      </Section>

      {/* Как продавец */}
      <Section title="Как продавец">
        <div className={styles.statsGrid}>
          <Stat label="Создано" value={me.auctions_created} />
          <Stat label="Продано" value={me.auctions_sold} variant="accent" />
          <Stat label="Рейтинг" value={me.rating.toFixed(1)} />
          <Stat
            label="Средняя цена"
            value={me.avg_sale_price > 0 ? formatMoney(me.avg_sale_price) : '—'}
            size="sm"
          />
        </div>
      </Section>

      {/* Подписки */}
      <Section title={`Подписки · ${subs?.length ?? 0}`}>
        {subs && subs.length > 0 ? (
          <div className={styles.subsList}>
            {subs.map((u) => (
              <Link
                key={u.id}
                to={`/user/${u.id}`}
                className={styles.subRow}
              >
                <Avatar src={u.avatar_url} name={u.display_name} size="md" />
                <div className={styles.subInfo}>
                  <p className={styles.subName}>{u.display_name}</p>
                  <p className={styles.subMeta}>
                    <span className={styles.subRating}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                      {u.rating.toFixed(1)}
                    </span>
                    · {u.auctions_sold} продаж
                  </p>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.subArrow}>
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState
            title="Нет подписок"
            description="Подписывайся на продавцов, чтобы первым видеть их новые аукционы"
          />
        )}
      </Section>

      {/* Отзывы о тебе */}
      <Section title={`Отзывы · ${me.rating_count}`}>
        <ReviewsList userId={me.id} />
      </Section>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className={styles.section}>
    <p className={styles.sectionTitle}>{title}</p>
    {children}
  </section>
);